import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RewardStatus } from '@core/enums/reward.enum';
import { Pagination } from '@core/models/pagination.model';
import { PlayerReward } from '@core/models/player-reward.model';
import { Rewards } from '@core/models/rewards.model';
import { AuthHttpService } from '@core/services/auth-http.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { RewardsHttpService } from '@core/services/rewards-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import * as moment from 'moment-timezone';
import { Observable, of, Subscription, throwError, timer } from 'rxjs';
import { map, tap, catchError, exhaustMap, switchMap, takeWhile } from 'rxjs/operators';
import { MemberInformationDialogComponent } from '../members/dialogs/member-information/member-information.component';
import { MemberDataService } from '../members/services/member-data.service';
import { MemberRewardsDialogComponent } from './../members/dialogs/member-rewards/member-rewards.component';
import { RewardDetailsDialogComponent } from './dialogs/reward-details/reward-details.component';
import { CurrencyHttpService } from '@core/services/currency-http.service';
import { ImportRewardsDialogComponent } from './dialogs/import-rewards/import-rewards.component';
import { RewardsDataService } from './services/rewards-data.service';
import { RewardMemberEligibleDialogComponent } from './dialogs/reward-member-eligible/reward-member-eligible.component';
import { AppPermissionService } from '@core/services/app-permission.service';
import Swal from 'sweetalert2';
import { Store, select } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'kt-rewards',
  templateUrl: './rewards.component.html',
  styleUrls: ['./rewards.component.scss']
})
export class RewardsComponent implements OnInit, OnDestroy {
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  form: FormGroup;
  dropdownSettings = {};
  dropdown = {
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    perPage: this.dropdownHttpService.perPage
  };

  status = RewardStatus
  statusColor = {
    0: 12,
    1: 1,
    2: 6,
    3: 7,
    4: 8,
  };

  rewards$: Observable<PlayerReward[]>;
  messages$ = this.rewardsHttpService.messages$;

  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  ranges: any;
  sortingStorageName = 'sortingConfig';
  sortingStorageGroup = '3.7';
  sortingConfig = {
    'id': 'desc',
    'username': 'desc',
    'promo_code': 'desc',
    'currency_code': 'desc',
    'credit': 'desc',
    'status': 'desc',
    'claimed_at': 'desc',
    'expiry_date': 'desc',
    'remarks': 'desc',
    'created_at': 'desc',
    'updated_at': 'desc'
  };
  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  buttonLoading = false;
  dataLength: number;

  accountTypeDropdownSettings = {};
  accountTypeDropdownList = [];
  accountTypeSelectedItems = [];

  isAdminUser = JSON.parse(localStorage.getItem('user_data')).is_admin;

  // permissions
  // rewardsUseNewPermissions: boolean;
  canViewMemberDialog: boolean;
  canAssignReward: boolean;
  canEditReward: boolean;
  canImportMemberRewards: boolean;
  canExportRewards: boolean;
  canCheckPromotionEligibility: boolean;

  dateTimeFilterType = [
    {
      key: 'claimed_at',
      label: 'Claimed Date'
    },
    {
      key: 'expiry_at',
      label: 'Expiry Date'
    },
    {
      key: 'created_at',
      label: 'Created Date'
    },
    {
      key: 'updated_at',
      label: 'Updated Date'
    }
  ]

  constructor(
    public dialog: MatDialog,
    private loadingBar: LoadingBarService,
    private dropdownHttpService: DropdownHttpService,
    private transactionHttpService: TransactionHttpService,
    private memberDataService: MemberDataService,
    private rewardsHttpService: RewardsHttpService,
    private eventEmitterService: EventEmitterService,
    private authHttpService: AuthHttpService,
    private currencyHttpService: CurrencyHttpService,
    private appPermissionService: AppPermissionService,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private rewardsDataService: RewardsDataService,
  ) { }

  private datePickerSubscription = new Subscription();
  private subscription = new Subscription();
  private subscriptions = new Subscription();

  ngOnInit() {
    this.setCurrencyDropdown();
    localStorage.setItem('sortingConfig', JSON.stringify({ [this.sortingStorageGroup]: this.sortingConfig }));
    this.formInit();
    this.pagination = this.rewardsHttpService.pagination;
    this.ranges = this.transactionHttpService.ranges;
    this.dropdownSettings = {
      singleSelection: true,
      text: 'Please Select',
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: 'dropdown',
      primaryKey: 'username',
      labelKey: 'username',
      lazyLoading: true,
      noDataLabel: '',
      showCheckbox: false
    };

    this.accountTypeDropdownList = this.dropdownHttpService.accountTypesLists;
    this.accountTypeSelectedItems = [
      { id: 1, name: 'Normal'},
      { id: 2, name: 'With-Label'},
      { id: 3, name: 'Dummy'}
    ];

    this.accountTypeDropdownSettings = {
      singleSelection: false,
      text: this.translateService.instant('Please Select'),
      maxHeight: 200,
      enableFilterSelectAll: true,
      enableSearchFilter: true,
      classes: 'dropdown',
      primaryKey: 'id',
      labelKey: 'name',
      lazyLoading: true,
      noDataLabel: '',
      showCheckbox: false
    };

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canViewMemberDialog = appPermissions.rewards_view_member_dialog;
      this.canAssignReward = appPermissions.assign_rewards;
      this.canEditReward = appPermissions.edit_reward;
      this.canImportMemberRewards = appPermissions.import_member_rewards;
      this.canExportRewards = appPermissions.export_rewards_list;
      this.canCheckPromotionEligibility = appPermissions.check_promotion_eligibility;
    });

    this.subscriptions.add(apSub);

    this.checkAutoLoad();
  }

  ngOnDestroy() {
    this.datePickerSubscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.rewards$ = this.rewardsHttpService.getPlayerRewards(`?page=${page}&perPage=${pageSize}&${this.generateSortingParam()}${params}`).pipe(
      tap(res => {
        this.loading = false;
        this.clearBtnLoading = false;
        this.searchBtnLoading = false;
        this.dataLength = res.length;
        this.pagination = this.rewardsHttpService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.page = 1;
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onOpenDialog(type: string, data?: number | Rewards) {
    switch (type) {
      case 'member-information':
        if (!this.canViewMemberDialog) {
          Swal.fire('Permission Error', 'You do not have permission to view member information', 'error');
          return;
        }

        const member = this.memberDataService.getById(+data, `?start_date=${moment(this.transactionHttpService.getYesterday().from).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss')}&end_date=${moment(this.transactionHttpService.getLast24Hours().to).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss')}&additional_info=1`);
        this.subscription = member.pipe(
          tap((res) => {
            if (res) {
              this.openDialogBy(MemberInformationDialogComponent, { member: res, mode: 'member-information' });
            }
          })
        )
          .subscribe();
        break;

      case 'edit':
        this.openDialogBy(RewardDetailsDialogComponent, {
          reward: (data as Rewards), mode: type
        });
        break;
      case 'create':
        this.openDialogBy(MemberRewardsDialogComponent, { member: null, mode: 'bulk' });
        break;
      case 'import':
        this.openDialogBy(ImportRewardsDialogComponent, { member: null, mode: null });
        break;
      case 'promo-eligible':
        this.openDialogBy(RewardMemberEligibleDialogComponent, { reward: (data as Rewards), mode: type });
        break;
    }

  }


  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.searchBtnLoading = clearSearch ? false : true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      tap((data) => {
        if (data['start_date_time']) {
          data['start_date_time'] = moment(data['start_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_date_time'] = moment(data['end_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        if(data['account_type']){
          data['account_type'] = this.form.controls['account_type'].value.toString();
        }

        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.page = 1;
        this.onViewPageBy();
      })
    ).subscribe();
  }

  onExport() {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.buttonLoading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        if (data['start_date_time']) {
          data['start_date_time'] = moment(data['start_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_date_time'] = moment(data['end_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.page = 1;
        const params = this.params ? `&${this.params}` : '';
        this.loadingBar.start();
        return this.rewardsHttpService.export(`?perPage=${this.pageSize}&${this.generateSortingParam()}${params}`).pipe(
          tap(res => {
            this.loadingBar.complete();
            this.buttonLoading = false;
          }),
          catchError(err => {
            this.loadingBar.complete();
            this.buttonLoading = false;
            return throwError(err);
          })
        );
      }),
    ).subscribe();
  }

  onClearDate() {
    if (this.form.value.start_date_time !== null) {
      this.form.patchValue({ defaultDate: null });
    }
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_date_time: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_date_time: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  onClear() {
    this.clearBtnLoading = true;
    this.eventEmitterService.onClearMemberSearch();
    this.accountTypeSelectedItems = [
      { id: 1, name: 'Normal'},
      { id: 2, name: 'With-Label'},
      { id: 3, name: 'Dummy'}
    ];
    this.form.patchValue({
      username: null,
      exact_username: false,
      status: 'all',
      start_date_time: null,
      end_date_time: null,
      promo_code: null,
      currency_id: 'all',
      defaultDate: null,
      account_type_condition: 'Or',
      account_type: [1, 2, 3],
      date_type: this.dateTimeFilterType[0].key
    });
    this.onSubmit(true);
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }

      }
    }
    // User selection
    this.sortingSelection.sort_by = property;

    if (this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }
    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  toLowerCaseInput(controlName: string, event: Event) {
    this.authHttpService.forceLowerCaseInputControl(this.form, controlName, event);
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  private openDialogBy(componentRef: any, data?: { member?: any, mode?: any, isAdmin?: boolean, reward?: Rewards }) {
    const dialogRef = this.dialog.open(componentRef, {
      width: data.mode === 'member-information' || 'promo-eligible' ? '1500px' : '800px',
      height: data.mode === 'member-information' ? '80vh' : 'auto',
      data,
      autoFocus: data.mode === 'member-information' ? false : true
    });
    if (data.mode !== 'member-information' && data.mode !== 'promo-eligible') {
      dialogRef.afterClosed().subscribe((result) => {
        if (typeof result === 'number') {
          // import_id returned — poll in background and show result modal (CR3)
          this.startImportPoll(result);
        } else if (result === true) {
          this.onViewPageBy(this.page);
        }
      });
    }
  }

  private startImportPoll(importId: number) {
    const INTERVAL = 4000;
    const TIMEOUT = 5 * 60 * 1000;
    const startedAt = Date.now();

    const sub = timer(INTERVAL, INTERVAL).pipe(
      switchMap(() => this.rewardsDataService.getImport(importId)),
      takeWhile((data: any) => {
        const status = data?.progress_status;
        const overTimeout = (Date.now() - startedAt) > TIMEOUT;
        return (status === 'pending' || status === 'processing') && !overTimeout;
      }, true),
    ).subscribe((data: any) => {
      const status = data?.progress_status;
      if (status === 'completed' || status === 'failed') {
        this.onViewPageBy(this.page);
        this.showImportResultSwal(data);
      }
    });

    this.subscriptions.add(sub);
  }

  private showImportResultSwal(data: any) {
    let summary: any = {};
    try {
      summary = typeof data?.summary === 'string' ? JSON.parse(data.summary) : (data?.summary ?? {});
    } catch (e) { summary = {}; }

    const failed = data?.progress_status === 'failed';
    const icon: any = failed ? 'error' : 'success';
    const total = data?.total_data ?? 0;
    const success = data?.success_data ?? 0;
    const headerMsg = failed
      ? (data?.remark || this.translateService.instant('Import failed.'))
      : `${success} / ${total} ` + this.translateService.instant('members successfully imported.');

    const failedMembers = [
      ...(summary.members_not_found ?? []),
      ...(summary.abnormal_status ?? []),
      ...(summary.currency_not_available ?? []),
      ...(summary.member_have_campaign ?? []),
      ...(summary.duplicate_members ?? []),
    ];
    const textareaList = failedMembers.join('\n');

    let html = `<p>${headerMsg}</p>`;
    if (textareaList) {
      html += `<br><div style="text-align:left;">
        <label>${this.translateService.instant('Members failed to assign rewards')}:
          <button id="btn-copy-result" style="all:unset;cursor:pointer;" title="${this.translateService.instant('Copy to clipboard')}">
            <i class="fas fa-copy"></i>
          </button>
        </label><br>
        <textarea style="width:100%;" rows="5" readonly>${textareaList}</textarea>
      </div>`;
    }

    Swal.fire({
      title: this.translateService.instant('Import Complete!'),
      html,
      icon,
      heightAuto: false,
      didOpen: () => {
        const btn = document.getElementById('btn-copy-result');
        if (btn) {
          btn.onclick = () => navigator.clipboard.writeText(textareaList).then(() => {
            const i = btn.querySelector('i');
            btn.setAttribute('title', this.translateService.instant('Copied!'));
            if (i) i.className = 'fas fa-check text-success';
            setTimeout(() => {
              btn.setAttribute('title', this.translateService.instant('Copy to clipboard'));
              if (i) i.className = 'fas fa-copy';
            }, 1500);
          }).catch(() => {});
        }
      },
    });
  }

  private setCurrencyDropdown() {
    if (this.dropdown.currencies.length === 0) {
      // set interval to get currencies from sessionStorage, to prevent returning empty currency dropdown
      if (sessionStorage.getItem('currencies') != undefined && sessionStorage.getItem('currencies') != null && sessionStorage.getItem('currencies') !== '') {
        var interval = setInterval(() => {
          this.dropdown.currencies = JSON.parse(sessionStorage.getItem('currencies'));
          if (this.dropdown.currencies != null) {
            clearInterval(interval);
          }
        }, 100);
      } else {
        this.currencyHttpService.setCurrency().subscribe(res => {
          this.dropdown.currencies = res;
        });
      }
    }
  }

  private formInit() {
    this.form = new FormGroup({
      username: new FormControl(null),
      exact_username: new FormControl(false),
      status: new FormControl(window.history.state.status !== undefined ? window.history.state.status : 'all'),
      start_date_time: new FormControl(null),
      end_date_time: new FormControl(null),
      promo_code: new FormControl(null),
      currency_id: new FormControl('all'),
      defaultDate: new FormControl(null), // Do not remove: For Clearing The Range
      account_type_condition: new FormControl('Or'),
      account_type: new FormControl([1, 2, 3]),
      date_type: new FormControl(this.dateTimeFilterType[0].key)
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined && key !== 'defaultDate' && formData[key] !== 'all') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  private isValidDate(date: Date | string) {
    if (moment(date).isValid()) {
      return true;
    }
    return false;
  }

  private checkAutoLoad() {
    const autoSearchKey = 'reward_auto_search_by_id';
    let reward_str = localStorage.getItem(autoSearchKey);
    if( reward_str ) {
      let reward = JSON.parse(reward_str);
      this.autoLoad(reward.username, reward.promotion_code);
      localStorage.removeItem(autoSearchKey);
    }
  }

  private autoLoad(username: string, promotion_code: string) {
    if( !this.loading ) {
      this.form.patchValue({
        username: username,
        promo_code: promotion_code,
      });
      this.onSubmit();
    }
  }
}
